import React, { useState, useEffect, useRef } from "react";

const HorrorClockGameWithGhost = () => {
  const [score, setScore] = useState(0);
  const [targetHour, setTargetHour] = useState(3);
  const [targetMinute, setTargetMinute] = useState(30);
  const [hourAngle, setHourAngle] = useState(0);
  const [minuteAngle, setMinuteAngle] = useState(0);
  const [result, setResult] = useState("");
  const [showNumbers, setShowNumbers] = useState(false);

  const clockRef = useRef(null);
  const dragging = useRef(null);

  // Audio
  const correctSound = useRef(new Audio("https://cdn.pixabay.com/download/audio/2022/03/15/audio_e1c8b7ed6e.mp3"));
  // const wrongSound = useRef(new Audio("https://cdn.pixabay.com/download/audio/2022/03/16/audio_0d139d2629.mp3"));
  const spookySound = useRef(new Audio("https://www.soundjay.com/clock/sounds/clock-ticking-1.mp3")); // loop
  const clockTick = useRef(new Audio("https://www.soundjay.com/clock/sounds/clock-ticking-1.mp3"));

  useEffect(() => {
    clockTick.current.loop = true;
    clockTick.current.volume = 0.2;
    clockTick.current.play().catch(() => {});
    clockTick.current.loop = true;
    clockTick.current.volume = 0.5;
    clockTick.current.play().catch(() => {});
  }, []);

  const getHourFromAngle = (angle) => Math.floor(((angle % 360) / 30 + 12) % 12);
  const getMinuteFromAngle = (angle) => Math.floor(((angle % 360) / 6 + 60) % 60);

  const loadNewTime = () => {
    const newHour = Math.floor(Math.random() * 12);
    const newMinute = Math.floor(Math.random() * 12) * 5;
    setTargetHour(newHour);
    setTargetMinute(newMinute);
    setHourAngle(0);
    setMinuteAngle(0);
    setResult("");
  };

  const checkTime = () => {
    const hour = getHourFromAngle(hourAngle);
    const minute = getMinuteFromAngle(minuteAngle);

    const correct = hour === targetHour && minute === targetMinute;

    if (correct) {
      correctSound.current.play();
      setResult("✅ The ghost approves! New challenge...");
      setScore((s) => s + 1);
      setTimeout(loadNewTime, 1000);
    } else {
      // wrongSound.current.play();
      setResult(
        `💀 Wrong! You set ${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`
      );
    }
  };

  const handleMouseDown = (hand) => (e) => {
    e.preventDefault();
    dragging.current = hand;
  };

  const handleMouseMove = (e) => {
    if (!dragging.current || !clockRef.current) return;
    const rect = clockRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    const normalized = (angle + 360) % 360;

    if (dragging.current === "hour") setHourAngle(normalized);
    else if (dragging.current === "minute") setMinuteAngle(normalized);
  };

  const handleMouseUp = () => {
    dragging.current = null;
  };

  const startGame = () => {
    setScore(0);
    loadNewTime();
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    loadNewTime();
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <div
      style={{
        fontFamily: "'Creepster', cursive",
        textAlign: "center",
        background: "radial-gradient(circle at center, #0d0d0d, #1a0000)",
        color: "#ff3d3d",
        minHeight: "100vh",
        padding: "40px 20px",
        position: "relative",
        overflow: "hidden",
        animation: "backgroundMove 20s linear infinite",
      }}
    >
      {/* Background fog */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "radial-gradient(circle at center, rgba(255,0,0,0.1), rgba(0,0,0,0.9))",
          pointerEvents: "none",
          zIndex: 0,
          animation: "fogMove 30s ease-in-out infinite",
        }}
      ></div>

      <p style={{ fontSize: "2.2rem", color: "#ff6b6b", marginBottom: "10px" }}>
        Drag the hands to match the  time! 
      </p>

      <div style={{ fontSize: "1.5rem", margin: "20px 0", color: "#ff3d3d" }}>
        Target Time:{" "}
        <span style={{ fontWeight: "bold", color: "#ff6b6b" }}>
          {targetHour.toString().padStart(2, "0")}:
          {targetMinute.toString().padStart(2, "0")}
        </span>
      </div>

      <button
        onClick={() => setShowNumbers(!showNumbers)}
        style={{
          marginBottom: "10px",
          background: "#8b0000",
          color: "#fff",
          borderRadius: "10px",
          padding: "8px 20px",
          border: "none",
          cursor: "pointer",
        }}
      >
        {showNumbers ? "Hide Numbers" : "Show Numbers"}
      </button>

      <div
        ref={clockRef}
        style={{
          position: "relative",
          width: "360px",
          height: "360px",
          margin: "30px auto",
          borderRadius: "50%",
          background: "radial-gradient(circle at 30% 30%, #1a0000, #000000 90%)",
          boxShadow: "0 0 70px #ff0000, inset 0 0 30px #330000",
          border: "5px solid #ff0000",
          zIndex: 1,
        }}
      >
        {/* Numbers */}
        {showNumbers &&
          [...Array(12)].map((_, i) => {
            const angle = (i * 30 - 90) * (Math.PI / 180);
            const radius = 150;
            const x = 180 + radius * Math.cos(angle) - 12;
            const y = 180 + radius * Math.sin(angle) - 12;
            const num = i === 0 ? 12 : i;
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  top: y,
                  left: x,
                  fontSize: "20px",
                  color: "#ff3d3d",
                  fontWeight: "700",
                  textShadow: "0 0 10px red",
                }}
              >
                {num}
              </div>
            );
          })}

        {/* Hour hand */}
        <div
          onMouseDown={handleMouseDown("hour")}
          style={{
            position: "absolute",
            bottom: "50%",
            left: "50%",
            width: "12px",
            height: "90px",
            background: "#ff0000",
            borderRadius: "10px",
            boxShadow: "0 0 30px red, 0 0 60px #8b0000",
            transformOrigin: "bottom center",
            transform: `rotate(${hourAngle}deg)`,
            cursor: "grab",
            zIndex: 2,
          }}
        />

        {/* Minute hand */}
        <div
          onMouseDown={handleMouseDown("minute")}
          style={{
            position: "absolute",
            bottom: "50%",
            left: "50%",
            width: "6px",
            height: "130px",
            background: "#00ff00",
            borderRadius: "4px",
            boxShadow: "0 0 30px #00ff00, 0 0 60px #00ff00",
            transformOrigin: "bottom center",
            transform: `rotate(${minuteAngle}deg)`,
            cursor: "grab",
            zIndex: 1,
          }}
        />

        {/* Center pin */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "20px",
            height: "20px",
            background: "#ff0000",
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: "0 0 40px red, 0 0 70px #8b0000",
            animation: "pulse 1s infinite alternate",
            zIndex: 3,
          }}
        />

        <style>
          {`
            @keyframes pulse {
              0% { transform: translate(-50%, -50%) scale(1); }
              100% { transform: translate(-50%, -50%) scale(1.3); }
            }
            @keyframes backgroundMove {
              0% { background-position: 0% 0%; }
              100% { background-position: 100% 100%; }
            }
            @keyframes fogMove {
              0% { transform: translateX(0); }
              50% { transform: translateX(30px); }
              100% { transform: translateX(0); }
            }
          `}
        </style>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "20px" }}>
        <button
          onClick={checkTime}
          style={{
            background: "linear-gradient(90deg, #ff0000, #8b0000)",
            border: "none",
            padding: "12px 30px",
            borderRadius: "30px",
            color: "white",
            fontSize: "1.1rem",
            cursor: "pointer",
            boxShadow: "0 0 20px #ff0000",
          }}
        >
          ✅ Check
        </button>
        <button
          onClick={startGame}
          style={{
            background: "linear-gradient(90deg, #4b0082, #8b00ff)",
            border: "none",
            padding: "12px 30px",
            borderRadius: "30px",
            color: "white",
            fontSize: "1.1rem",
            cursor: "pointer",
            boxShadow: "0 0 20px #8b00ff",
          }}
        >
          🔄 Restart
        </button>
      </div>

      <div
        style={{
          marginTop: "25px",
          fontSize: "1.3rem",
          color: "#ff6b6b",
          textShadow: "0 0 10px red",
        }}
      >
        {result}
      </div>

      <div style={{ marginTop: "20px", fontSize: "1.2rem", color: "#ff3d3d" }}>🏆 Score: {score}</div>
    </div>
  );
};

export default HorrorClockGameWithGhost;
